import React, { useState, useEffect } from 'react';

const AdminContacts = () => {
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('all');
    const [showDetails, setShowDetails] = useState(null);

    const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

    const getAuthHeaders = React.useCallback(() => {
        const token = localStorage.getItem('adminToken');
        return {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
        };
    }, []);

    const loadContacts = React.useCallback(async () => {
        try {
            const response = await fetch(`${API_URL}/api/contacts`, {
                headers: getAuthHeaders()
            });
            const data = await response.json();
            setContacts(data);
        } catch (error) {
            console.error('Error loading contacts:', error);
        } finally {
            setLoading(false);
        }
    }, [API_URL, getAuthHeaders]);

    useEffect(() => {
        loadContacts();
    }, [loadContacts]);

    const updateStatus = async (id, status) => {
        try {
            const response = await fetch(`${API_URL}/api/contacts/${id}`, {
                method: 'PUT',
                headers: getAuthHeaders(),
                body: JSON.stringify({ status })
            });

            if (response.ok) {
                loadContacts();
                alert('Contact status updated!');
            }
        } catch (error) {
            console.error('Error updating contact:', error);
            alert('Error updating contact');
        }
    };

    const deleteContact = async (id) => {
        if (!window.confirm('Are you sure you want to delete this contact?')) return;

        try {
            const response = await fetch(`${API_URL}/api/contacts/${id}`, {
                method: 'DELETE',
                headers: getAuthHeaders()
            });

            if (response.ok) {
                loadContacts();
                alert('Contact deleted!');
            }
        } catch (error) {
            console.error('Error deleting contact:', error);
            alert('Error deleting contact');
        }
    };

    const filteredContacts = contacts.filter(contact => {
        if (filter === 'all') return true;
        return contact.status === filter;
    });

    const getStatusColor = (status) => {
        switch (status) {
            case 'new': return 'bg-blue-100 text-blue-800';
            case 'replied': return 'bg-green-100 text-green-800';
            case 'closed': return 'bg-gray-100 text-gray-800';
            default: return 'bg-gray-100 text-gray-800';
        }
    };

    if (loading) {
        return <div className="text-center py-8">Loading...</div>;
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-3xl font-bold">Contacts Management</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setFilter('all')}
                        className={`px-4 py-2 rounded ${filter === 'all' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        All ({contacts.length})
                    </button>
                    <button
                        onClick={() => setFilter('new')}
                        className={`px-4 py-2 rounded ${filter === 'new' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
                    >
                        New ({contacts.filter(c => c.status === 'new').length})
                    </button>
                    <button
                        onClick={() => setFilter('replied')}
                        className={`px-4 py-2 rounded ${filter === 'replied' ? 'bg-green-600 text-white' : 'bg-gray-200'}`}
                    >
                        Replied ({contacts.filter(c => c.status === 'replied').length})
                    </button>
                </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Subject</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {filteredContacts.length === 0 ? (
                            <tr>
                                <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                    No contacts found
                                </td>
                            </tr>
                        ) : (
                            filteredContacts.map(contact => (
                                <tr key={contact.id}>
                                    <td className="px-6 py-4 whitespace-nowrap font-semibold">{contact.name}</td>
                                    <td className="px-6 py-4">{contact.email}</td>
                                    <td className="px-6 py-4">{contact.subject || 'No subject'}</td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        {new Date(contact.createdAt).toLocaleDateString()}
                                    </td>
                                    <td className="px-6 py-4">
                                        <select
                                            value={contact.status}
                                            onChange={(e) => updateStatus(contact.id, e.target.value)}
                                            className={`px-2 py-1 rounded ${getStatusColor(contact.status)}`}
                                        >
                                            <option value="new">New</option>
                                            <option value="replied">Replied</option>
                                            <option value="closed">Closed</option>
                                        </select>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <button
                                            onClick={() => setShowDetails(contact)}
                                            className="text-blue-600 hover:text-blue-800 mr-3"
                                            title="View Details"
                                        >
                                            <i className="fas fa-eye"></i>
                                        </button>
                                        <button
                                            onClick={() => deleteContact(contact.id)}
                                            className="text-red-600 hover:text-red-800"
                                            title="Delete"
                                        >
                                            <i className="fas fa-trash"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Details Modal */}
            {showDetails && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-2xl font-bold">Contact Details</h3>
                            <button onClick={() => setShowDetails(null)} className="text-gray-500 hover:text-gray-700">
                                <i className="fas fa-times text-2xl"></i>
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="font-semibold">Name:</label>
                                    <p>{showDetails.name}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Email:</label>
                                    <p>{showDetails.email}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Phone:</label>
                                    <p>{showDetails.phone || 'N/A'}</p>
                                </div>
                                <div>
                                    <label className="font-semibold">Date:</label>
                                    <p>{new Date(showDetails.createdAt).toLocaleString()}</p>
                                </div>
                                <div className="col-span-2">
                                    <label className="font-semibold">Subject:</label>
                                    <p>{showDetails.subject || 'No subject'}</p>
                                </div>
                            </div>
                            <div>
                                <label className="font-semibold">Message:</label>
                                <p className="mt-1 p-3 bg-gray-50 rounded whitespace-pre-wrap">{showDetails.message}</p>
                            </div>
                        </div>
                        <div className="mt-6 flex justify-end space-x-3">
                            <a
                                href={`mailto:${showDetails.email}`}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                            >
                                <i className="fas fa-reply mr-2"></i>Reply via Email
                            </a>
                            <button
                                onClick={() => setShowDetails(null)}
                                className="px-6 py-2 bg-gray-200 rounded-lg hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminContacts;
